/**
 * Retail product browser for the POS: scan-first search, dense variant-aware rows,
 * a pinned quick pad, an optional tile view and price-check mode.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Search, ScanLine, List, Grid, Star, Camera, Tag, Package, X, RefreshCw, Store, Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { api } from "@/store/useStore";
import { resolveImageUrl } from "@/lib/utils";
import {
  fetchCatalog, readCachedCatalog, searchCatalog, findByCode, serverSearch
} from "@/lib/pos-catalog";

const VIEW_KEY = "hostpoint_pos_view_mode";

export default function RetailBrowser({ outletId, onAdd, formatCurrency }) {
  const [catalog, setCatalog] = useState({ products: [], categories: [], groups: [] });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [query, setQuery] = useState("");
  const [categoryId, setCategoryId] = useState("all");
  const [viewMode, setViewMode] = useState(() => localStorage.getItem(VIEW_KEY) || "list");
  const [priceCheck, setPriceCheck] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [favourites, setFavourites] = useState([]);
  const [serverResults, setServerResults] = useState(null);
  const [searchingServer, setSearchingServer] = useState(false);
  const [stockElsewhere, setStockElsewhere] = useState({});
  const [priceCheckItem, setPriceCheckItem] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const searchRef = useRef(null);
  const videoRef = useRef(null);

  const fmt = formatCurrency || ((n) => `KES ${(n || 0).toLocaleString()}`);

  // Load cached catalogue first, then refresh in the background
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cached = await readCachedCatalog(outletId);
      if (cached && !cancelled) { setCatalog(cached); setLoading(false); }
      try {
        const fresh = await fetchCatalog(outletId);
        if (!cancelled) setCatalog(fresh);
      } catch (e) {
        if (!cached && !cancelled) toast.error("Could not load the catalogue");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [outletId]);

  useEffect(() => {
    api.get(`/pos/favourites?outlet_id=${encodeURIComponent(outletId || "")}`)
      .then((data) => setFavourites(data.product_ids || []))
      .catch(() => setFavourites([]));
  }, [outletId]);

  useEffect(() => { localStorage.setItem(VIEW_KEY, viewMode); }, [viewMode]);

  const focusSearch = useCallback(() => searchRef.current?.focus(), []);

  // A keyboard-wedge scanner types into whatever has focus — keep that the search box
  useEffect(() => {
    const onKey = (e) => {
      const tag = document.activeElement?.tagName;
      const typingElsewhere = tag === "INPUT" || tag === "TEXTAREA" || document.activeElement?.isContentEditable;
      if (e.key === "F1") { e.preventDefault(); focusSearch(); return; }
      if (typingElsewhere) return;
      if (/^[a-zA-Z0-9]$/.test(e.key)) focusSearch();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [focusSearch]);

  const { results, total } = useMemo(
    () => searchCatalog(catalog.products || [], query, { categoryId }),
    [catalog.products, query, categoryId]
  );

  // Server fallback when the local index comes up empty
  useEffect(() => {
    setServerResults(null);
    if (!query || query.trim().length < 2 || total > 0) return;
    const timer = setTimeout(async () => {
      setSearchingServer(true);
      try {
        setServerResults(await serverSearch(query.trim(), outletId));
      } catch (e) {
        setServerResults([]);
      } finally {
        setSearchingServer(false);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [query, total, outletId]);

  const rows = serverResults?.length ? serverResults : results;

  const favouriteProducts = useMemo(
    () => (catalog.products || []).filter((p) => favourites.includes(p.id)),
    [catalog.products, favourites]
  );

  const addToSale = (product, variant = null, quantity = 1) => {
    if (priceCheck) {
      setPriceCheckItem({ product, variant });
      setQuery("");
      focusSearch();
      return;
    }
    onAdd(product, variant, quantity);
    if (navigator.vibrate) navigator.vibrate(12);
    setQuery("");
    setExpandedId(null);
    focusSearch();
  };

  const handleSelect = (product) => {
    if (product.has_variants) {
      setExpandedId((prev) => (prev === product.id ? null : product.id));
      return;
    }
    addToSale(product);
  };

  const handleScanValue = (code) => {
    const hit = findByCode(catalog.products || [], code);
    if (hit) {
      addToSale(hit.product, hit.variant);
      return true;
    }
    return false;
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") { setQuery(""); setExpandedId(null); return; }
    if (e.key !== "Enter") return;
    e.preventDefault();
    const code = query.trim();
    if (!code) return;
    if (handleScanValue(code)) return;
    if (rows.length === 1) { handleSelect(rows[0]); return; }
    if (rows.length === 0) toast.error(`Nothing matches “${code}”`);
  };

  const toggleFavourite = async (productId) => {
    const next = favourites.includes(productId)
      ? favourites.filter((id) => id !== productId)
      : [...favourites, productId];
    setFavourites(next);
    try {
      await api.put(`/pos/favourites?outlet_id=${encodeURIComponent(outletId || "")}`, { product_ids: next });
    } catch (e) {
      toast.error("Could not save the quick pad");
    }
  };

  const loadStockElsewhere = async (product, variant) => {
    const key = variant ? `${product.id}:${variant.id}` : product.id;
    if (stockElsewhere[key]) { setStockElsewhere((prev) => ({ ...prev, [key]: null })); return; }
    try {
      const params = new URLSearchParams({ product_id: product.id });
      if (variant) params.append("variant_id", variant.id);
      const data = await api.get(`/pos/stock-elsewhere?${params.toString()}`);
      setStockElsewhere((prev) => ({ ...prev, [key]: data.locations || [] }));
    } catch (e) {
      toast.error("Could not check other outlets");
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      setCatalog(await fetchCatalog(outletId));
      toast.success("Catalogue refreshed");
    } catch (e) {
      toast.error("Refresh failed — using the cached catalogue");
    } finally {
      setRefreshing(false);
    }
  };

  // ---- camera scanning (tablets / phones) ----
  useEffect(() => {
    if (!cameraOpen) return;
    let stream;
    let stop = false;
    (async () => {
      if (!("BarcodeDetector" in window)) {
        toast.error("This device's browser can't scan with the camera — use a USB scanner");
        setCameraOpen(false);
        return;
      }
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        // eslint-disable-next-line no-undef
        const detector = new BarcodeDetector({ formats: ["ean_13", "ean_8", "upc_a", "code_128"] });
        const tick = async () => {
          if (stop || !videoRef.current) return;
          try {
            const found = await detector.detect(videoRef.current);
            if (found.length > 0) {
              const code = found[0].rawValue;
              if (handleScanValue(code)) { setCameraOpen(false); return; }
              toast.error(`Unknown barcode ${code}`);
            }
          } catch (e) { /* keep scanning */ }
          setTimeout(tick, 350);
        };
        tick();
      } catch (e) {
        toast.error("Camera permission denied");
        setCameraOpen(false);
      }
    })();
    return () => {
      stop = true;
      stream?.getTracks().forEach((track) => track.stop());
    };
  }, [cameraOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const variantGrid = (product) => {
    const attrs = product.variant_attributes || [];
    const rowAttr = attrs[0];
    const colAttr = attrs[1];
    if (!rowAttr) return null;

    if (!colAttr) {
      return (
        <div className="flex flex-wrap gap-2 p-3 bg-muted/40" data-testid={`variant-grid-${product.id}`}>
          {product.variants.map((v) => (
            <Button
              key={v.id}
              size="sm"
              variant={v.stock > 0 ? "outline" : "ghost"}
              disabled={v.stock <= 0}
              className="h-10"
              onClick={() => addToSale(product, v)}
              data-testid={`variant-cell-${v.id}`}
            >
              {v.label} <span className="ml-2 text-xs text-muted-foreground">{v.stock}</span>
            </Button>
          ))}
        </div>
      );
    }

    return (
      <div className="p-3 bg-muted/40 overflow-x-auto" data-testid={`variant-grid-${product.id}`}>
        <table className="text-sm">
          <thead>
            <tr>
              <th className="text-left pr-3 pb-2 text-xs uppercase tracking-wide text-muted-foreground">
                {rowAttr.name} / {colAttr.name}
              </th>
              {colAttr.values.map((col) => (
                <th key={col} className="px-1 pb-2 text-xs font-medium">{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rowAttr.values.map((row) => (
              <tr key={row}>
                <td className="pr-3 py-1 font-medium">{row}</td>
                {colAttr.values.map((col) => {
                  const variant = product.variants.find(
                    (v) => v.attributes?.[rowAttr.name] === row && v.attributes?.[colAttr.name] === col
                  );
                  const out = !variant || variant.stock <= 0;
                  return (
                    <td key={col} className="px-1 py-1">
                      <button
                        disabled={out}
                        onClick={() => variant && addToSale(product, variant)}
                        data-testid={variant ? `variant-cell-${variant.id}` : undefined}
                        className={`w-14 h-10 rounded-md border text-xs font-medium transition-colors ${
                          out
                            ? "border-dashed text-muted-foreground/60 cursor-not-allowed"
                            : "border-primary/40 hover:bg-primary hover:text-primary-foreground"
                        }`}
                      >
                        {variant ? variant.stock : "—"}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <Button
          variant="link"
          size="sm"
          className="mt-1 px-0"
          onClick={() => loadStockElsewhere(product, null)}
          data-testid={`stock-elsewhere-${product.id}`}
        >
          <Store className="h-3 w-3 mr-1" /> Stock by size / other outlets
        </Button>
        {stockElsewhere[product.id] && (
          <div className="text-xs text-muted-foreground mt-1" data-testid={`stock-elsewhere-list-${product.id}`}>
            {stockElsewhere[product.id].length === 0
              ? "No other stock found"
              : stockElsewhere[product.id].map((loc) => `${loc.name}: ${loc.quantity}`).join(" · ")}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Scan / search bar */}
      <div className="p-3 border-b bg-background/95 space-y-2">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <ScanLine className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-primary" />
            <Input
              ref={searchRef}
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Scan barcode or type name, SKU or brand…"
              className="pl-11 h-12 text-base"
              data-testid="pos-scan-input"
            />
            {query && (
              <button
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                onClick={() => { setQuery(""); focusSearch(); }}
                data-testid="pos-clear-search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <Button variant="outline" size="icon" className="h-12 w-12" onClick={() => setCameraOpen(true)} data-testid="pos-camera-scan">
            <Camera className="h-5 w-5" />
          </Button>
          <Button
            variant={priceCheck ? "default" : "outline"}
            size="icon"
            className="h-12 w-12"
            onClick={() => { setPriceCheck((v) => !v); setPriceCheckItem(null); focusSearch(); }}
            title="Price check mode"
            data-testid="pos-price-check-toggle"
          >
            <Tag className="h-5 w-5" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-12 w-12"
            onClick={() => setViewMode(viewMode === "list" ? "tiles" : "list")}
            title="Switch view"
            data-testid="pos-view-toggle"
          >
            {viewMode === "list" ? <Grid className="h-5 w-5" /> : <List className="h-5 w-5" />}
          </Button>
          <Button variant="ghost" size="icon" className="h-12 w-12" onClick={refresh} disabled={refreshing} data-testid="pos-refresh-catalog">
            {refreshing ? <Loader2 className="h-5 w-5 animate-spin" /> : <RefreshCw className="h-5 w-5" />}
          </Button>
        </div>

        <div className="flex items-center gap-2 overflow-x-auto pb-1">
          <Button
            variant={categoryId === "all" ? "default" : "outline"}
            size="sm"
            className="shrink-0 h-8"
            onClick={() => setCategoryId("all")}
            data-testid="pos-category-all"
          >
            All
          </Button>
          {(catalog.categories || []).map((cat) => (
            <Button
              key={cat.id}
              variant={categoryId === cat.id ? "default" : "outline"}
              size="sm"
              className="shrink-0 h-8"
              onClick={() => setCategoryId(cat.id)}
              data-testid={`pos-category-${cat.id}`}
            >
              {cat.name}
            </Button>
          ))}
        </div>

        {priceCheck && (
          <div className="text-xs text-primary font-medium" data-testid="pos-price-check-banner">
            Price check mode — scanning shows the price instead of adding to the sale
          </div>
        )}
      </div>

      {priceCheckItem && (
        <div className="m-3 p-4 rounded-lg border bg-card" data-testid="pos-price-check-result">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Price check</p>
          <p className="text-lg font-semibold">{priceCheckItem.product.name}</p>
          {priceCheckItem.variant && <p className="text-sm">{priceCheckItem.variant.label}</p>}
          <p className="text-2xl font-bold text-primary mt-1">
            {fmt(priceCheckItem.variant?.price ?? priceCheckItem.product.price)}
          </p>
          <p className="text-sm text-muted-foreground">
            In stock: {priceCheckItem.variant ? priceCheckItem.variant.stock : priceCheckItem.product.stock}
          </p>
          <Button variant="outline" size="sm" className="mt-2" onClick={() => setPriceCheckItem(null)}>Close</Button>
        </div>
      )}

      {cameraOpen && (
        <div className="m-3 rounded-lg overflow-hidden border relative" data-testid="pos-camera-view">
          <video ref={videoRef} className="w-full h-56 object-cover bg-black" muted playsInline />
          <Button size="sm" variant="secondary" className="absolute top-2 right-2" onClick={() => setCameraOpen(false)}>
            Stop
          </Button>
        </div>
      )}

      {/* Quick pad */}
      {!query && favouriteProducts.length > 0 && (
        <div className="px-3 pt-3">
          <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Quick pad</p>
          <div className="flex flex-wrap gap-2" data-testid="pos-quick-pad">
            {favouriteProducts.map((product) => (
              <Button
                key={product.id}
                variant="secondary"
                className="h-12"
                onClick={() => handleSelect(product)}
                data-testid={`quick-pad-${product.id}`}
              >
                {product.name}
                <span className="ml-2 text-xs opacity-70">{fmt(product.price)}</span>
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Results */}
      <ScrollArea className="flex-1">
        {loading ? (
          <div className="p-8 text-center text-muted-foreground">Loading catalogue…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground" data-testid="pos-no-results">
            {searchingServer ? "Searching…" : query ? `Nothing matches “${query}”` : "No products yet"}
          </div>
        ) : viewMode === "tiles" ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 p-3" data-testid="pos-tiles">
            {rows.map((product) => (
              <button
                key={product.id}
                onClick={() => handleSelect(product)}
                className="pos-item-card p-2 text-left"
                data-testid={`pos-tile-${product.id}`}
              >
                <div className="w-full h-20 rounded-md overflow-hidden mb-2 bg-muted flex items-center justify-center">
                  {product.image_url ? (
                    <img
                      src={resolveImageUrl(product.image_url)}
                      alt={product.name}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  ) : (
                    <Package className="h-7 w-7 text-muted-foreground" />
                  )}
                </div>
                <p className="text-sm font-medium line-clamp-2">{product.name}</p>
                <p className="text-sm font-mono text-primary">{fmt(product.price)}</p>
              </button>
            ))}
          </div>
        ) : (
          <div className="divide-y" data-testid="pos-list">
            {rows.map((product) => (
              <div key={product.id}>
                <div
                  className="flex items-center gap-3 px-3 py-2.5 hover:bg-accent cursor-pointer"
                  onClick={() => handleSelect(product)}
                  data-testid={`pos-row-${product.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">
                      {product.name}
                      {product.brand && <span className="text-muted-foreground text-sm"> · {product.brand}</span>}
                    </p>
                    <p className="text-xs text-muted-foreground font-mono truncate">
                      {product.sku}{product.barcode ? ` · ${product.barcode}` : ""}
                    </p>
                  </div>
                  {product.has_variants && (
                    <Badge variant="outline" className="shrink-0">{product.variants.length} sizes</Badge>
                  )}
                  <div className="text-right shrink-0">
                    <p className="font-semibold text-primary">{fmt(product.price)}</p>
                    <p className={`text-xs ${product.stock <= 0 ? "text-destructive" : "text-muted-foreground"}`}>
                      {product.stock <= 0 ? "Out of stock" : `${product.stock} in stock`}
                    </p>
                  </div>
                  <button
                    className="shrink-0 p-1"
                    onClick={(e) => { e.stopPropagation(); toggleFavourite(product.id); }}
                    data-testid={`pin-product-${product.id}`}
                    title="Pin to quick pad"
                  >
                    <Star className={`h-4 w-4 ${favourites.includes(product.id) ? "fill-amber-400 text-amber-400" : "text-muted-foreground"}`} />
                  </button>
                </div>
                {expandedId === product.id && product.has_variants && variantGrid(product)}
              </div>
            ))}
            {total > rows.length && (
              <p className="p-3 text-xs text-muted-foreground text-center">
                Showing {rows.length} of {total} matches — keep typing to narrow it down
              </p>
            )}
          </div>
        )}
      </ScrollArea>

      <div className="border-t px-3 py-1.5 text-[11px] text-muted-foreground flex items-center gap-3">
        <Search className="h-3 w-3" /> F1 search · F2 qty · F3 discount · F4 hold · F9 pay
        <span className="ml-auto">{catalog.product_count || (catalog.products || []).length} items cached</span>
      </div>
    </div>
  );
}
